import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Listbox } from "@headlessui/react";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

import { FnrPopoverOptionsList } from "UI/FnrPopover2";
import { colors } from "UI/colors";
import { scrollbarDarkThin } from "UI/scrollbarStyles";

import { australiaStateOptions } from "Utilities/Address/australiaStateOptions";
import { canadaStateOptions } from "Utilities/Address/canadaStateOptions";
import { mexicoStateOptions } from "Utilities/Address/mexicoStateOptions";
import { peruStateOptions } from "Utilities/Address/peruStateOptions";
import { unitedStatesStateOptions } from "Utilities/Address/unitedStatesStateOptions";

import { useAddress } from "./AddressContext";
import { Label } from "./Styles";

StateField.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string,
  countryIDOrCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  onMenuChange: PropTypes.func,
  hideLabel: PropTypes.bool,
  buttonWidth: PropTypes.string,
  autoComplete: PropTypes.string,
  style: PropTypes.object,
  disabled: PropTypes.bool,
  shadow: PropTypes.string,
  onShadowCommit: PropTypes.func,
};

export function StateField({
  value,
  onChange,
  onMenuChange,
  countryIDOrCode = 1,
  hideLabel,
  style,
  name = "state",
  autoComplete = "off",
  disabled = false,
  shadow = "",
  onShadowCommit,
}) {
  const { clearStateFieldTrigger } = useAddress();
  const [menuOpen, setMenuOpen] = useState(false);
  // Drives the shadow color: the ghost suggestion keeps its lighter gray
  // while this field is active (button focused or menu open) and dims toward
  // black when it isn't — matching the inline-input ghosts (ShadowText).
  const [isFocused, setIsFocused] = useState(false);
  const [previewID, setPreviewID] = useState(null);
  const stateButtonRef = useRef();
  const hiddenInputRef = useRef();
  const popoverRef = useRef();
  const optionRefs = useRef({});
  // Type-ahead buffer + reset timer. Stored in refs so accumulating
  // letters does not trigger a re-render between keystrokes and so the
  // 500ms reset survives concurrent renders.
  const typeAheadBufferRef = useRef("");
  const typeAheadTimeoutRef = useRef(null);
  // When the user opens the menu, the click + popover transfer focus,
  // which fires a blur on the button. That blur must not silently commit
  // the shadow — opening the menu is intent to pick a different value.
  // Tracked via a ref so the blur handler sees the update synchronously
  // (no race with React's setMenuOpen propagation). Reset on focus so a
  // subsequent tab-through can still commit.
  const userOpenedMenuRef = useRef(false);

  // This dismissed the popover if user pressed "escape" while menu was open. However, caused a bug where
  // if you opened menu with keyboard, then clicked button with mouse to close, it stayed open.
  const onFocus = () => {
    userOpenedMenuRef.current = false;
    setIsFocused(true);
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => {
      if (!prev) userOpenedMenuRef.current = true;
      return !prev;
    });
  };

  // Idempotent close. Used as the popover's onShouldClose so that a stray
  // outside-event firing when the menu is already closed does not toggle it
  // back open. Mobile in particular fires multiple events per tap.
  const closeMenu = () => {
    setMenuOpen(false);
  };

  useEffect(() => {
    // This is how the state field clears when the country field is changed.
    if (clearStateFieldTrigger > 0) {
      onChange("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearStateFieldTrigger]);

  useEffect(() => {
    if (onMenuChange) onMenuChange(menuOpen);
  }, [menuOpen, onMenuChange]);

  useEffect(() => {
    return () => setMenuOpen(false);
  }, []);

  // Pre-scroll/preselect the shadow option when the menu opens without
  // a committed value, so a single Enter commits the autocomplete
  // suggestion the user is already seeing in the field.
  useEffect(() => {
    if (!menuOpen) {
      setPreviewID(null);
      return;
    }
    if (previewID) return;
    if (!shadow || value) return;
    const match = stateOptions.find((s) => s.ID === shadow);
    if (!match) return;
    // Defer to after the popover mounts so option refs exist.
    const id = setTimeout(() => activatePreview(match.ID), 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen, shadow, value]);

  const activatePreview = (id) => {
    setPreviewID(id);
    const el = optionRefs.current[id];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  };

  const runTypeAhead = (letter) => {
    if (typeAheadTimeoutRef.current) {
      clearTimeout(typeAheadTimeoutRef.current);
    }
    typeAheadBufferRef.current = (
      typeAheadBufferRef.current + letter
    ).toLowerCase();
    typeAheadTimeoutRef.current = setTimeout(() => {
      typeAheadBufferRef.current = "";
    }, 500);

    const buffer = typeAheadBufferRef.current;
    // A full 2-letter abbreviation wins outright (typing "mo" → Missouri, not
    // Montana — whose NAME also prefixes "mo"). Otherwise name match wins
    // (typing "ill" → Illinois); fall back to ID prefix (typing "il" →
    // Illinois) so 2-letter code users aren't punished.
    const exactIdMatch =
      buffer.length === 2 &&
      stateOptions.find((s) => s.ID.toLowerCase() === buffer);
    const match =
      exactIdMatch ||
      stateOptions.find((s) => s.name.toLowerCase().startsWith(buffer)) ||
      stateOptions.find((s) => s.ID.toLowerCase().startsWith(buffer));
    if (!match) return;

    onChange(match.ID);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight" && shadow && !value && onShadowCommit) {
      e.preventDefault();
      onShadowCommit();
      return;
    }
    if (e.key === "Enter") {
      if (menuOpen && previewID) {
        e.preventDefault();
        const match = stateOptions.find((s) => s.ID === previewID);
        if (match) handleSelect(match);
        return;
      }
      setMenuOpen(true);
    } else if (e.key === "Escape") {
      setMenuOpen(false);
    } else if (e.keyCode >= 65 && e.keyCode <= 90) {
      e.stopPropagation();
      runTypeAhead(e.key);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (userOpenedMenuRef.current) return;
    if (shadow && !value && onShadowCommit) onShadowCommit();
  };

  const handleSelect = (newValue) => {
    if (disabled) return;
    onChange(newValue.ID);
    setMenuOpen(false);
  };

  const getStateIDFromNameOrAbbreviation = (stateNameOrAbbreviation) => {
    let stateID;
    let l = stateNameOrAbbreviation.length;
    // depending on how the state is saved, it can be the full name or the abbreviation
    if (l === 2) {
      stateID = stateNameOrAbbreviation.toUpperCase();
    } else {
      stateID = stateOptions.find(
        (s) => s.name.toLowerCase() === stateNameOrAbbreviation.toLowerCase()
      )?.ID;
    }
    return stateID;
  };

  const handleAutoComplete = (e) => {
    const stateID = getStateIDFromNameOrAbbreviation(e.target.value);
    onChange(stateID);
  };

  const stateOptions = STATE_OPTIONS_BY_COUNTRY_ID_OR_CODE[countryIDOrCode];
  if (!stateOptions) return null;

  const selectedState = stateOptions.find((s) => s.ID === value);

  const labelText =
    countryIDOrCode === 2 || countryIDOrCode === "CA" ? "Prov" : "State";

  const hasThreeLetterStateOptions = stateOptions.some(
    (option) => option?.ID.length === 3
  );

  return (
    <Wrapper data-cy="StateField_Container">
      <HiddenInput
        ref={hiddenInputRef}
        type="text"
        name={name}
        onChange={handleAutoComplete}
        tabIndex={-1}
        autoComplete={autoComplete}
        aria-hidden="true"
      />
      {/* iOS WebKit's address autofill writes only into <select> elements
          for the state field — a hidden <input> is excluded from the fill
          set entirely. This native select mirrors the visible Listbox so
          autofill can find a target it recognizes; the change handler
          forwards back into the controlled value. */}
      <HiddenSelect
        name={`${name}-autofill`}
        value={value || ""}
        onChange={(e) =>
          onChange(getStateIDFromNameOrAbbreviation(e.target.value))
        }
        autoComplete="address-level1"
        tabIndex={-1}
        aria-hidden="true"
      >
        <option value="" />
        {stateOptions.map((state) => (
          <option key={state.ID} value={state.ID}>
            {state.name}
          </option>
        ))}
      </HiddenSelect>
      <Listbox value={selectedState} onChange={handleSelect}>
        <>
          <Button
            ref={stateButtonRef}
            onClick={toggleMenu}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={handleBlur}
            style={style}
            role="button"
            $isWide={hasThreeLetterStateOptions}
          >
            <span>
              {hideLabel ? null : <StateLabel>{labelText}</StateLabel>}
              <StateValue $value={value}>
                <span>
                  {value ? (
                    value
                  ) : shadow ? (
                    <Ghost aria-hidden="true" $focused={isFocused || menuOpen}>
                      {shadow}
                    </Ghost>
                  ) : hideLabel ? (
                    labelText
                  ) : (
                    ""
                  )}{" "}
                </span>
                <ChevronWrapper menuOpen={menuOpen}>
                  <FontAwesomeIcon icon={faChevronDown} />
                </ChevronWrapper>
              </StateValue>
            </span>
          </Button>

          <FnrPopoverOptionsList
            show={menuOpen}
            onShouldClose={closeMenu}
            buttonRef={stateButtonRef}
          >
            <div ref={popoverRef}>
              <OptionList static onKeyDown={handleKeyDown}>
                {stateOptions.map((state) => {
                  const isPreview = previewID === state.ID;
                  return (
                    <Listbox.Option
                      key={state.ID}
                      value={state}
                      role="option"
                      ref={(el) => {
                        if (el) optionRefs.current[state.ID] = el;
                        else delete optionRefs.current[state.ID];
                      }}
                      data-preview-active={isPreview ? "true" : undefined}
                    >
                      {({ selected, active }) => (
                        <OptionInner
                          active={active || isPreview}
                          selected={selected}
                        >
                          {state.name}
                          <CheckmarkHolder>
                            {selected ? <span className="fa fa-check" /> : null}
                          </CheckmarkHolder>
                        </OptionInner>
                      )}
                    </Listbox.Option>
                  );
                })}
              </OptionList>
            </div>
          </FnrPopoverOptionsList>
        </>
      </Listbox>
    </Wrapper>
  );
}

const STATE_OPTIONS_BY_COUNTRY_ID_OR_CODE = Object.freeze({
  1: unitedStatesStateOptions,
  US: unitedStatesStateOptions,

  2: canadaStateOptions,
  CA: canadaStateOptions,

  3: australiaStateOptions,
  AU: australiaStateOptions,

  9: mexicoStateOptions,
  MX: mexicoStateOptions,

  10: peruStateOptions,
  PE: peruStateOptions,
});

/**
 * Whether a country has a state/province option list. Countries without one
 * (e.g. the UK, UAE, Antigua, Singapore, Bahamas) make StateField render
 * nothing, so a caller that requires a filled `state` for a "complete" address
 * must exempt them — else those addresses can never be completed.
 *
 * @param {number|string} countryIDOrCode - numeric country ID or ISO code
 * @returns {boolean}
 */
export const countryHasStateOptions = (countryIDOrCode) =>
  Boolean(STATE_OPTIONS_BY_COUNTRY_ID_OR_CODE[countryIDOrCode]);

const Wrapper = styled.div`
  position: relative;
`;

// Sized to overlay the visible dropdown button. Without an explicit size
// it would fall back to its intrinsic ~178px width and push the page wider
// than the viewport on mobile. pointer-events: none keeps taps going to
// the real button beneath.
const HiddenInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  border: 0;
  padding: 0;
  margin: 0;
`;

const HiddenSelect = styled.select`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  border: 0;
  padding: 0;
  margin: 0;
  appearance: none;
`;

const Button = styled(Listbox.Button)`
  background: transparent;
  border: none;
  border-right: 1px solid #e5e5e5;
  flex-shrink: 0;
  cursor: pointer;
  padding: 6px;
  width: ${({ $isWide }) => ($isWide ? "70px" : "60px")};
  transition: background-color 300ms;

  &:hover {
    background-color: #efefef;
  }

  &:focus {
    background-color: #efefef;
  }

  @media (max-width: 430px) {
    border-bottom: 1px solid #e5e5e5;
  }
`;

const StateValue = styled.div`
  display: flex;
  gap: 2px;
  justify-content: space-between;
  padding: 4px 0 4px 2px;
  color: ${({ $value }) => ($value ? "#222" : "rgb(117, 117, 117)")};
  font-weight: 400;
  font-size: 18px;
  font-family: "Open Sans", sans-serif;

  svg {
    padding-top: 6px;
    padding-left: 3px;
    color: ${colors.fnBlue};
    font-size: 14px;
  }
`;

const StateLabel = styled(Label)`
  text-align: left;
  cursor: pointer;
`;

// Focused/active: the lighter gray. Unfocused: dim toward black (a darker
// palette gray), matching the inline-input ghosts in ShadowText.
const Ghost = styled.span`
  color: ${({ $focused }) => ($focused ? colors.fnGray400 : colors.fnGray700)};
  opacity: 0.75;
`;

const ChevronWrapper = styled.div`
  width: 20px;
  transition: transform 200ms;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  transform-origin: 55% 60% 0;
  transform: ${({ menuOpen }) => (menuOpen ? "rotate(-180deg)" : null)};
`;

const OptionList = styled(Listbox.Options)`
  outline: none;
  max-height: 400px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  padding-right: 6px;
  list-style: none;
  ${scrollbarDarkThin}
`;

const OptionInner = styled.div`
  padding: 6px 10px;
  color: white;
  opacity: ${({ selected }) => (selected ? 1 : 0.9)};
  background: ${({ active }) => (active ? "rgba(255, 255, 255, 0.15)" : null)};
  display: flex;
  border-radius: 10px;
  cursor: pointer;
  transition: background 150ms;
`;

const CheckmarkHolder = styled.div`
  width: 24px;
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 4px;
`;
